import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';

const PublicStoreReceipt = ({ open, onClose, orderData, storeData }) => {
  const navigate = useNavigate();
  const { storeSlug } = useParams();
  const handlePrint = () => {
    window.print();
  };

  if (!orderData) return null;

  const orderNumber = String(orderData.orderId || orderData._id || '').slice(-8).toUpperCase();
  const items = orderData.items || [];
  const total = orderData.total || 0;
  const customer = orderData.customer || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
            <Typography variant="h6" fontWeight={700}>
              Order Confirmed!
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handlePrint} size="small" color="primary">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={700} color="success.main" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your order has been placed successfully
          </Typography>
        </Box>

        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Order Number
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            #{orderNumber}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Customer Details
          </Typography>
          <Typography variant="body2">
            <strong>Name:</strong> {customer.name || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Phone:</strong> {customer.phone || 'N/A'}
          </Typography>
          {customer.address && (
            <Typography variant="body2">
              <strong>Address:</strong> {customer.address}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Order Items
          </Typography>
          {items.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: idx < items.length - 1 ? '1px solid' : 'none',
                borderColor: 'grey.200'
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {item.name || 'Item'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Qty: {item.qty || 0} × {(item.price || 0).toFixed(2)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {(item.lineTotal || 0).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">Subtotal:</Typography>
          <Typography variant="body1">{(orderData.subtotal || 0).toFixed(2)}</Typography>
        </Box>

        {orderData.tax > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1">Tax:</Typography>
            <Typography variant="body1">₹{(orderData.tax || 0).toFixed(2)}</Typography>
          </Box>
        )}

        {orderData.deliveryCharge > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1">Delivery Fee:</Typography>
            <Typography variant="body1">₹{(orderData.deliveryCharge || 0).toFixed(2)}</Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'primary.main',
            color: 'white',
            p: 2,
            borderRadius: 1,
            mt: 2
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Total:
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            ₹{total.toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Payment Method: {orderData.paymentMode === 'ONLINE' ? 'Online Payment' : 'Pay at Counter'}
          </Typography>
          {orderData.paymentMode === 'COUNTER' && (
            <Typography variant="body2" color="warning.main" fontWeight={600} sx={{ mt: 1 }}>
              Please pay at the counter when you collect your order
            </Typography>
          )}
        </Box>

        {storeData?.storeName && (
          <Box sx={{ mt: 3, textAlign: 'center', borderTop: '1px solid', borderColor: 'grey.200', pt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {storeData.storeName}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={() => {
              onClose();
              navigate(`/store/${storeSlug}/track/${orderData.orderId}`);
            }}
            startIcon={<TrackChangesIcon />}
          >
            Track Order
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handlePrint}
              startIcon={<PrintIcon />}
            >
              Print Receipt
            </Button>
            <Button variant="outlined" fullWidth onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PublicStoreReceipt;
